import React, { useEffect, useMemo, useState } from "react";
import { App } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import AppControl from "../../../components/";
import Card from "../../../components/controls/Card";
import { createYupSchema } from "../../../utilities/";
import { getRoles } from "../../../store/slice/roles";
import { getUser, saveUser, updateUser } from "../../../store/slice/users";

const APPLICATION_NAME = "fixilo";
const FORM_NAMES = {
  EDIT: "EditUser",
  CREATE: "User",
};
const PAGE_TITLES = {
  EDIT: "EDIT USER",
  CREATE: "CREATE A NEW USER",
};

function UserAdd() {
  const [title, setTitle] = useState(null);
  const { id } = useParams();
  const { message } = App.useApp();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const pages = useSelector((state) => state.common.pages);
  const { isLoading, user } = useSelector((state) => state.users);
  const roleList = useSelector((state) => state.roles.list);

  // Determine current page configuration based on create vs edit mode
  const currentPageControls = useMemo(() => {
    if (!pages) return null;
    const formName = id ? FORM_NAMES.EDIT : FORM_NAMES.CREATE;
    const currentPage = pages.find((item) => item.formName === formName);
    return currentPage?.fields;
  }, [pages, id]);

  // Initialize data on mount
  useEffect(() => {
    dispatch(getRoles());
    if (id) {
      setTitle(PAGE_TITLES.EDIT);
      dispatch(getUser(id));
    } else {
      setTitle(PAGE_TITLES.CREATE);
    }
  }, [id, dispatch]);

  // Build dropdown values for role selector
  const dropdownValues = useMemo(() => {
    if (!roleList) return {};
    return {
      roles: roleList.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    };
  }, [roleList]);
  console.log("Dropdown values:", dropdownValues);
  // Build initial form values and validation schema
  const { initialValues, validationSchema } = useMemo(() => {
    if (!currentPageControls) {
      return { initialValues: {}, validationSchema: Yup.object().shape({}) };
    }

    let values = {};
    if (id && user) {
      values = currentPageControls.reduce(
        (acc, control) => ({
          ...acc,
          [control.fieldId]: user[control.fieldId],
        }),
        {},
      );
    } else {
      values = currentPageControls.reduce(
        (acc, control) => ({
          ...acc,
          [control.fieldId]: control.defaultValue,
        }),
        { roles: [] },
      );
    }

    const yupSchema = currentPageControls.reduce(createYupSchema, {});
    const schema = Yup.object().shape(yupSchema);

    return { initialValues: values, validationSchema: schema };
  }, [currentPageControls, user, id]);

  const handleSubmitSuccess = (response) => {
    message.success(response.message);
    navigate("/users");
  };

  const handleSubmitError = (error) => {
    const errorMessage = error.payload?.message || error.error?.message;
    message.error(`Save failed: ${errorMessage}`);
  };

  const handleSubmit = async (values) => {
    const submitData = {
      ...values,
      applicationName: APPLICATION_NAME,
      TenantNames: null,
      ...(id && { id: user.id }),
    };
    console.log("Submitting data:", submitData);
    const thunkCreator = id ? updateUser : saveUser;
    const resultAction = await dispatch(thunkCreator(submitData));

    if (thunkCreator.fulfilled.match(resultAction)) {
      console.log("Submit successful:", resultAction);
      handleSubmitSuccess(resultAction.payload);
    } else {
      console.error("Submit failed:", resultAction);
      handleSubmitError(resultAction);
    }
  };

  const renderFormFields = () => (
    <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-2">
      {currentPageControls?.map((control) => (
        <AppControl
          key={control.fieldId}
          control={control.type}
          label={control.label}
          name={control.fieldId}
          placeholder={control.placeholder}
          options={dropdownValues[control.fieldId] ?? control.options}
        />
      ))}
    </div>
  );

  const renderFormActions = () => (
    <div className="border-t-2 border-neutral-100 pt-3">
      <AppControl
        control="button"
        type="submit"
        name="Save"
        size="md"
        color="orange"
        disabled={!Formik.isValid || isLoading}
        loading={isLoading}
      />
      <AppControl
        control="button"
        type="reset"
        name="Cancel"
        size="md"
        color="blue"
        onClick={() => navigate("/users")}
      />
    </div>
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Card title={title}>
          <Form>
            {renderFormFields()}
            {renderFormActions()}
          </Form>
        </Card>
      )}
    </Formik>
  );
}

export default UserAdd;
